// The real constellations on the home half, drawn like the Figma atlas
// figures: hairlines in the line color and the four-point atlas star at
// each vertex. They ride the near star layer, wrap with the field, and fade
// with the home UI. On the planisphere (the stage's chart) each star turns
// into a chart star, a dot in a ring, and the lines into fine star-cool.
import { memo, useLayoutEffect, useRef, useState } from 'react';
import { useStore } from '../../systems/store';
import { ambientSettings } from './ambient/ambientSettings';
import type { AmbientConfig } from './ambient/ambientConfig';
import { ATLAS_FIGURES, atlasStarRadius, type AtlasFigure } from './atlasFigures';
import { tileSpan, wrapIndex } from './drift';
import type { SkyMotion } from './skyMotion';
import { TILE_COUNT, TILE_WIDTH } from './weekdaySkies';

interface Props {
  motion: SkyMotion;
  scale: number;
  visibleWidth: number;
  shown: boolean;
  fadeMs: number;
}

export function AtlasLayer({ motion, scale, visibleWidth, shown, fadeMs }: Props) {
  const cfg = useStore(ambientSettings).constellations;
  const ref = useRef<HTMLDivElement>(null);
  const [first, setFirst] = useState(0);
  useLayoutEffect(() => motion.addLayer(ref.current!, 2, setFirst), [motion]);
  const placed: Array<{ key: string; figure: AtlasFigure; x: number }> = [];
  for (let k = first; k < first + tileSpan(visibleWidth, TILE_WIDTH); k++) {
    const bucket = wrapIndex(k, TILE_COUNT);
    for (const figure of ATLAS_FIGURES) {
      if (Math.floor(figure.x / TILE_WIDTH) === bucket) placed.push({ key: `${k}-${figure.name}`, figure, x: k * TILE_WIDTH + (figure.x - bucket * TILE_WIDTH) });
    }
  }
  return (
    <div ref={ref} className="sky-layer sky-atlas" data-shown={shown} style={{ transitionDuration: `${fadeMs}ms` }}>
      {placed.map((p) => (
        <Figure key={p.key} figure={p.figure} x={p.x} scale={scale} cfg={cfg} />
      ))}
    </div>
  );
}

const Figure = memo(function Figure({ figure, x, scale, cfg }: { figure: AtlasFigure; x: number; scale: number; cfg: AmbientConfig['constellations'] }) {
  const hw = figure.halfWidth * scale;
  const hh = figure.halfHeight * scale;
  return (
    <svg
      className="atlas-figure"
      style={{ left: x * scale - hw, top: figure.y * scale - hh, width: 2 * hw, height: 2 * hh }}
      aria-label={figure.name}
    >
      <g transform={`translate(${hw} ${hh})`}>
        {figure.lines.map(([a, b]) => {
          const sa = figure.stars[a]!;
          const sb = figure.stars[b]!;
          return (
            <line
              key={`${a}-${b}`}
              className="atlas-line"
              x1={sa.x * scale}
              y1={sa.y * scale}
              x2={sb.x * scale}
              y2={sb.y * scale}
              stroke="var(--line)"
              strokeOpacity={cfg.opacity}
              strokeWidth={cfg.lineWidth}
            />
          );
        })}
        {figure.stars.map((s, i) => (
          <g key={i} transform={`translate(${s.x * scale} ${s.y * scale})`}>
            <path className="atlas-symbol" d={fourPoint(atlasStarRadius(s.mag, cfg.starSize))} fill="var(--star-white)" />
            <g className="atlas-chart-star">
              <circle r="4.6" />
              <circle r="2" />
            </g>
          </g>
        ))}
      </g>
    </svg>
  );
});

/** The atlas star symbol: four slim points. */
function fourPoint(o: number): string {
  const i = o * 0.2;
  return `M0 ${-o} L${i} ${-i} L${o} 0 L${i} ${i} L0 ${o} L${-i} ${i} L${-o} 0 L${-i} ${-i} Z`;
}
